import { QueryClient } from '@tanstack/react-query';
import { System } from '@wavingroup/aqora-v2-crate/wavin/aqora/v2/system_pb';
import { SystemPermission_Permission } from '@wavingroup/aqora-v2-crate/wavin/aqora/v2/user_pb';
import { LoaderFunctionArgs, redirect } from 'react-router';
import { listOpenIssuesForSystemQuery } from '~/shared/api/issues.queries';
import { waterLevelsMetricsQuery } from '~/shared/api/metrics.queries';
import { getSystemQuery } from '~/shared/api/system.queries';
import { listSystemDesignAlertsQuery } from '~/shared/api/systemDesignAlerts.queries';
import {
  checkAuthorization,
  checkIfUserHasPermissionForSystem,
} from '~/shared/auth/auth-utils';
import { IssuesLoaderData } from '~/shared/hooks/issues-hooks';
import { createSystemDesignRoute } from '~/shared/models/create-routes';
import { WaterLevelsModel } from '~/shared/models/metrics/waterLevelsModel/WaterLevelsModel';
import { ResourceNameIdConverter } from '~/shared/models/ResourceNameIdConverter';
import { SystemDesignAlertsModel } from '~/shared/models/system/SystemDesignAlertsModel';
import { SystemModel } from '~/shared/models/system/SystemModel';
import { assertIsDefined } from '~/types/assert-type';

export type SystemPageLoaderResult = IssuesLoaderData & {
  system: System;
  waterLevels: WaterLevelsModel;
  systemDesignAlerts?: SystemDesignAlertsModel;
};

export const systemLoader =
  (queryClient: QueryClient) =>
  async ({
    params,
    request,
  }: LoaderFunctionArgs): Promise<SystemPageLoaderResult | Response> => {
    await checkAuthorization(queryClient, request);

    const { systemId } = params;

    assertIsDefined(systemId);

    const [system, listIssuesResponse] = await Promise.all([
      queryClient.ensureQueryData(getSystemQuery(systemId)),
      queryClient.ensureQueryData(
        listOpenIssuesForSystemQuery({
          systemName: ResourceNameIdConverter.systemIdToName(systemId),
        }),
      ),
    ]);
    const systemModel = new SystemModel({
      system,
      issuesResponse: listIssuesResponse,
    });

    const userHasDesignPermissions = await checkIfUserHasPermissionForSystem(
      queryClient,
      request,
      systemId,
      [
        SystemPermission_Permission.DESIGN,
        SystemPermission_Permission.COMMISSIONING,
      ],
    );

    const systemInDesignPhase =
      systemModel.state === 'CONFIGURATION' ||
      systemModel.state === 'PRODUCTION_EDIT' ||
      systemModel.state === 'ADVICE';

    if (userHasDesignPermissions && systemInDesignPhase) {
      return redirect(createSystemDesignRoute(systemModel.id));
    }

    const shouldShowSystemDesignAlerts =
      systemModel.state === 'READY_FOR_INSTALL' && userHasDesignPermissions;

    const systemReservoirs = systemModel.getReservoirNames();

    const [waterLevels, systemDesignAlerts] = await Promise.all([
      queryClient.ensureQueryData(
        waterLevelsMetricsQuery({ reservoirNames: systemReservoirs }),
      ),
      shouldShowSystemDesignAlerts
        ? queryClient.ensureQueryData(listSystemDesignAlertsQuery(system.name))
        : undefined,
    ]);

    return {
      system,
      issues: listIssuesResponse,
      waterLevels,
      systemDesignAlerts,
    };
  };
